import { logError } from '@/lib/logging';

interface EnvConfig {
  apiBaseUrl: string;
  isDevelopment: boolean;
  isProduction: boolean;
}

interface EnvValidationError {
  variable: string;
  message: string;
}

declare global {
  interface Window {
    __ENV__?: Record<string, string>;
  }
}

function getEnvVar(key: string): string | undefined {
  if (typeof window !== 'undefined' && window.__ENV__ && key in window.__ENV__) {
    const runtimeValue: unknown = window.__ENV__[key];
    if (typeof runtimeValue === 'string' && runtimeValue.length > 0) {
      return runtimeValue;
    }
  }
  const value: unknown = import.meta.env[key];
  return typeof value === 'string' ? value : undefined;
}

function validateRequiredEnvVars(): EnvValidationError[] {
  const errors: EnvValidationError[] = [];

  const apiBaseUrl = getEnvVar('VITE_API_BASE_URL');
  if (!apiBaseUrl) {
    errors.push({
      variable: 'VITE_API_BASE_URL',
      message: 'API base URL is required. Set VITE_API_BASE_URL in your .env file.',
    });
  }

  return errors;
}

function formatValidationErrors(errors: EnvValidationError[]): string {
  const errorList = errors.map((e) => `  - ${e.variable}: ${e.message}`).join('\n');

  return `Environment configuration validation failed:\n${errorList}\n\nSee .env.example for required variables.`;
}

function createEnvConfig(): EnvConfig {
  const errors = validateRequiredEnvVars();

  if (errors.length > 0) {
    const errorMessage = formatValidationErrors(errors);
    logError(new Error(errorMessage), {
      operation: 'createEnvConfig',
      metadata: { missingVars: errors.map((e) => e.variable) },
    });
    throw new Error(errorMessage);
  }

  const apiBaseUrl = getEnvVar('VITE_API_BASE_URL');

  if (!apiBaseUrl) {
    throw new Error('Required environment variables are missing after validation');
  }

  const mode = import.meta.env.MODE;

  return {
    apiBaseUrl,
    isDevelopment: mode === 'development',
    isProduction: mode === 'production',
  };
}

export const env = createEnvConfig();
